import { memo, useCallback, useMemo } from 'react';
import { CircularProgress, Typography } from '@mui/material';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { useTranslation } from 'react-i18next';
import { useToggle } from '@dfl/hook-utils';
import { IconButton, LoadingButton } from '@dfl/mui-react-common';
import { PermissionCheck } from '@dfl/react-security';
import { IOrder } from '../../interfaces/IOrder';
import { SUBORDER_PERMISSIONS } from '../../constants/order-permissions';
import useSuborderComplete from '../../hooks/useSuborderComplete';
import { ORDER_STATUS_TYPE_ENUM } from 'modules/sales/settings/order-status/constants';
import { SUB_ORDER_ERRORS } from 'modules/sales/sub-orders/constants/sub-order.errors';
import OrderConfirmComplete from '../OrderCompleteButton/OrderConfirmComplete';

type Props = {
  order: IOrder;
  variant?: 'button' | 'icon';
};

const SuborderCompleteButton = ({ order, variant = 'button' }: Props) => {
  const { t } = useTranslation('order');
  const { isOpen, onClose, onOpen } = useToggle(false);
  const { control, onSubmit, isLoading, error, reset } = useSuborderComplete(order?._id as string);
  const disabled = useMemo(
    () =>
      order?.status?.type === ORDER_STATUS_TYPE_ENUM.FRAUD ||
      order?.status?.type === ORDER_STATUS_TYPE_ENUM.CANCELED ||
      order?.status?.type === ORDER_STATUS_TYPE_ENUM.REFUNDED ||
      order?.status?.type === ORDER_STATUS_TYPE_ENUM.PENDING_REFUNDED ||
      order?.status?.type === ORDER_STATUS_TYPE_ENUM.COMPLETED,
    [order?.status?.type],
  );

  const handleOpen = useCallback(() => {
    reset();
    onOpen();
  }, [onOpen, reset]);

  return (
    <PermissionCheck permissions={SUBORDER_PERMISSIONS.SUBORDER_WRITE}>
      {variant === 'button' ? (
        <LoadingButton
          variant='contained'
          loading={isLoading}
          startIcon={<DoneAllIcon />}
          onClick={handleOpen}
          disabled={disabled}
        >
          <Typography noWrap>{t('header.actions.suborderComplete')}</Typography>
        </LoadingButton>
      ) : (
        <IconButton tooltip={t('header.actions.suborderComplete')} onClick={handleOpen} disabled={disabled}>
          {isLoading ? <CircularProgress size={20} /> : <DoneAllIcon />}
        </IconButton>
      )}

      <OrderConfirmComplete
        onSubmit={onSubmit}
        control={control}
        open={isOpen}
        title={'header.actions.suborderComplete'}
        onClose={onClose}
        isLoading={isLoading}
        error={error}
        errors={SUB_ORDER_ERRORS}
      />
    </PermissionCheck>
  );
};

export default memo(SuborderCompleteButton);
